import React from "react";
import { Link } from "react-router-dom";
// The design team's own tile, pulled in as a component so a className can size
// it. It draws its own tinted square and the camera inside it, so nothing here
// has a colour of its own to keep in step with it.
import { ReactComponent as VideoIcon } from "../../../assets/svg/support/video icon.svg";
// The mark over the empty history. Like the tile above, it draws its own tinted
// disc and the headset inside it, so nothing here picks a colour for it.
import { ReactComponent as NoHistoryMark } from "../../../assets/svg/support/support_no_data_image.svg";
import {
  Button,
  EmptyState,
  SkeletonCard,
  buttonClasses,
  skeletonRows,
  useLoadPhase,
} from "../../../components/ui";
import { ROUTES } from "../../../routes/paths";
import { isUpcomingStatus } from "../types/support.types";
import type { SupportMeeting, SupportMeetingStatus } from "../types/support.types";

/** No border on these: they sit inside a row, not beside a page heading. The
 *  same pair the payments table wears, plus the purple the app already uses
 *  for something that has not happened yet — and, for a call that has moved,
 *  the teal the clients table wears, which is a second word for a call still
 *  ahead and reads as one rather than as a warning. */
const STATUS_STYLES: Record<SupportMeetingStatus, string> = {
  Scheduled: "bg-[#EDE7F6] text-[#5B3FA8]",
  Rescheduled: "bg-[#E6FAFB] text-[#00899B]",
  Completed: "bg-[#E7F8EF] text-[#2F8E5B]",
  Cancelled: "bg-[#F1F4F6] text-ink-muted",
};

/** A line in the middle of the row, between what the booking is and when it is:
 *  what this says, then what it says. The heading is inked and the words after
 *  it muted, so a row carrying both of these is read as two answers rather than
 *  as a paragraph. */
const RowNote: React.FC<{ heading: string; children: React.ReactNode }> = ({
  heading,
  children,
}) => (
  <p className="mt-0.5 text-[13px] 3xl:text-[14px] text-ink-muted">
    {/* The colon rides with the heading rather than sitting between the two as
        its own child, so the heading is one text node and can be found by the
        words it actually reads as. */}
    <span className="font-medium text-ink">{`${heading}:`}</span>{" "}
    {children}
  </p>
);

/**
 * When a call is, written out in full: `14 Aug, 2026, 11:30 AM, Friday`.
 *
 * The day is spelled out at the end because it is what people actually plan
 * around — "Friday" is the half of a date somebody checks against their week,
 * and it is the half a numeric date makes them work out.
 *
 * Built by hand rather than through `toLocaleTimeString`, which puts a narrow
 * no-break space before the AM on newer ICU builds and an ordinary one on
 * older: invisible either way on the screen, and the difference between a test
 * that passes and one that does not.
 */
export const formatMeetingWhen = (iso: string): string => {
  const date = new Date(iso);

  const day = date.getDate();
  const month = date.toLocaleDateString("en-GB", { month: "short" });
  const weekday = date.toLocaleDateString("en-GB", { weekday: "long" });

  const hours = date.getHours();
  const suffix = hours < 12 ? "AM" : "PM";
  // Midnight and noon are 12, not 0 — `% 12` alone would write "0:15 AM".
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  const time = `${hour12}:${String(date.getMinutes()).padStart(2, "0")} ${suffix}`;

  return `${day} ${month}, ${date.getFullYear()}, ${time}, ${weekday}`;
};

/** One booking: what it is, when, and — while it is still ahead — what can be
 *  done about it. */
const MeetingRow: React.FC<{ meeting: SupportMeeting }> = ({ meeting }) => {
  // Only a call that is still coming can be joined or moved — a moved one as
  // much as an untouched one, which is what `isUpcomingStatus` is for. The
  // links are checked as well as the status, because a booking that has lost
  // its room is the one case where a button would be there and lead nowhere.
  const upcoming = isUpcomingStatus(meeting.status);

  return (
    <li className="flex flex-wrap items-center gap-3 card-surface border border-line bg-white px-4 py-3.5 sm:gap-4 sm:px-5 3xl:px-6">
      {/* Marked as a video call by its icon rather than by a word: the row
          already says what it is, and every one of these is a call. */}
      {/* Sized on the wrapper rather than on the glyph, so the artwork keeps its
          own proportions whatever this is set to. */}
      <span
        aria-hidden="true"
        className="h-[42px] w-[42px] flex-shrink-0 3xl:h-12 3xl:w-12"
      >
        <VideoIcon className="h-full w-full" />
      </span>

      {/* `min-w-0` so a long date wraps inside this column instead of pushing
          the buttons off the end of the row. */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {/* What the call is about, which is the reason picked off the
              booking form's list — so a history of four calls reads as four
              subjects rather than four identical lines. It comes back as the
              first half of the booking's notes; see `splitNotes`.

              Only that half. The paragraph under it is something somebody
              typed: it wraps, it trails off, and it would push the pill beside
              it onto a second line, so it stays below and this line stays the
              short fixed thing the eye runs down a list on.

              "Meeting booked" where there is no reason — a booking made
              through Cal's own pages rather than our form. Still a title, and
              still true. */}
          <span className="text-[14px] 3xl:text-[15px] font-medium text-ink">
            {meeting.reason || "Meeting booked"}
          </span>
          <span
            className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${STATUS_STYLES[meeting.status]}`}
          >
            {meeting.status}
          </span>
        </div>
        {/* What they wrote under that reason, in their own words.

            In the middle of the row, under the title and over the date, and on
            every row whatever the status. It is what the title is about, so it
            follows it and comes before the arrangements; the date keeps the
            last line, where it has always been and where the eye running down a
            list of calls finds it.

            Unheaded, because the line above it is already the heading: the
            reason titles the row and this is the paragraph under that reason,
            which is how it was written and how it reads. A "Notes" label
            between the two would name what the layout already says.

            Except on a call that was called off, where the reason it was called
            off sits directly below. Two unlabelled paragraphs would leave the
            reader working out which of them the cancelling was about, so on
            those rows both take a heading and the pair reads as two answers.

            Absent where the booking was made without one, which is most moved
            bookings — Cal starts the new one clean rather than carrying the
            note across. */}
        {meeting.notes &&
          (meeting.status === "Cancelled" ? (
            <RowNote heading="Notes">{meeting.notes}</RowNote>
          ) : (
            <p className="mt-0.5 text-[13px] 3xl:text-[14px] text-ink-muted">
              {meeting.notes}
            </p>
          ))}

        {/* Why it is off, where whoever called it off said. The pill already
            says that it is, so this is the half of the answer the pill cannot
            give — and it is nowhere else on the screen. Headed in full rather
            than as a bare "Reason", now that a note can sit directly above it
            and two unlabelled paragraphs would leave the reader sorting out
            which of them the cancelling was about.

            After the note rather than before it: what the call was for was
            written first and is what the cancelling is about, so the two read
            in the order they happened. */}
        {meeting.cancellationReason && (
          <RowNote heading="Cancellation reason">
            {meeting.cancellationReason}
          </RowNote>
        )}

        {/* Last, and the one line every row has. */}
        <p className="mt-1 text-[13px] 3xl:text-[14px] text-ink-muted">
          {formatMeetingWhen(meeting.startsAt)}
        </p>
      </div>

      {/* Two equal halves of a line of their own on a phone. The pair is
          already on its own line there — an icon, a date and two buttons do not
          share 360px — and left at their own widths they huddle against the
          left edge, half a row from the thumb. From `md` up they are the pair
          at the end of the row the design has. */}
      {upcoming && (meeting.rescheduleUid || meeting.joinUrl) && (
        <div className="flex w-full flex-shrink-0 items-center gap-2 md:w-auto sm:gap-3">
          {/* Links, not buttons: both go somewhere, and going somewhere is a
              link's job. See `buttonClasses`, which is what lets them look like
              the pair they are without a second copy of the styles. */}
          {/* Moving a call is picking a time, which is the screen Book call
              already opens — so Modify goes there rather than out to cal.com,
              carrying the booking it is moving. Router state rather than the
              URL, as the booking screen's other caller does it: a booking id is
              nobody's business in a browser history. */}
          {meeting.rescheduleUid && (
            <Link
              to={ROUTES.bookCall}
              // The time as well as the id: that screen says what is being
              // moved above the booker, and this row is the only place it is
              // known — the booker itself keeps Cal's note of the former time
              // on a panel nobody scrolls to.
              state={{
                rescheduleUid: meeting.rescheduleUid,
                currentStartsAt: meeting.startsAt,
              }}
              className={buttonClasses({
                variant: "secondary",
                size: "sm",
                className: "flex-1 md:flex-none",
              })}
            >
              Modify
            </Link>
          )}
          {/* The video room stays a new tab: it is somewhere else by nature,
              and a call somebody is about to join should not take the screen
              they were reading. */}
          {meeting.joinUrl && (
            <a
              href={meeting.joinUrl}
              target="_blank"
              rel="noreferrer"
              className={buttonClasses({
                variant: "primary",
                size: "sm",
                className: "flex-1 md:flex-none",
              })}
            >
              Join meeting
            </a>
          )}
        </div>
      )}
    </li>
  );
};

export interface SupportHistoryProps {
  meetings: SupportMeeting[];
  /** Whether they are still on their way — see `useSupportHistory`. */
  loading?: boolean;
  /** What went wrong, where the history could not be read at all. */
  error?: string;
  /** Asks for them again, where a load failed. */
  onRetry?: () => void;
}

/** How many rows the placeholders stand in for. Two, because that is nearer the
 *  usual answer than a screenful: a practice books support a few times a year. */
const PLACEHOLDER_ROWS = 2;

/** The caption and the space under it, which every state of this section
 *  shares. */
const Section: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section className="mt-6 3xl:mt-8">
    {/* Captioned the way `FormSection` captions a card, so the two blocks on
        this tab read as one screen rather than two. */}
    <h3 className="mb-2 text-[13px] 3xl:text-[14px] text-ink-muted">
      Support history
    </h3>
    {children}
  </section>
);

/**
 * Every support call this workspace has booked, newest first.
 *
 * It sits under the booking form rather than on a screen of its own because it
 * is the answer to the question the form raises — "have I already asked?" — and
 * because there is never much of it: a practice books support a handful of
 * times a year, not a page of times a month.
 *
 * The section is always drawn, in every one of its four states — coming, come
 * to nothing, failed, and full. It used to disappear on the first three, and a
 * screen that shows nothing cannot say which kind of nothing it means: a
 * workspace that has never booked a call and one whose history failed to load
 * looked identical, and so did both of them and a tab whose request was never
 * made. The caption stays put and the space under it says which.
 */
const SupportHistory: React.FC<SupportHistoryProps> = ({
  meetings,
  loading = false,
  error,
  onRetry,
}) => {
  // Blank for the first moment and only then shimmered, as every waiting screen
  // in the app does it — a tab that answers quickly goes from empty to filled
  // with no shimmer in between. The caption is up either way now, so nothing
  // appears and leaves again.
  const phase = useLoadPhase(loading);

  if (loading) {
    return (
      <Section>
        {/* One block per row, not per line inside it: what a row turns out to
            hold is the row's business once it lands. */}
        <ul className="flex flex-col gap-3">
          {skeletonRows(PLACEHOLDER_ROWS).map((index) => (
            <li key={index}>
              {/* Taller on a phone, where the same booking is: the date takes a
                  line of its own and Modify and Join take another. */}
              <SkeletonCard
                phase={phase}
                className="h-[110px] md:h-[74px] 3xl:h-[82px]"
              />
            </li>
          ))}
        </ul>
      </Section>
    );
  }

  if (error) {
    return (
      <Section>
        <div className="flex flex-wrap items-center gap-3 card-surface border border-line bg-white px-4 py-3.5 sm:px-5 3xl:px-6">
          {/* The server's own words, as everywhere else a load fails. `status`
              so a reader who is not watching this corner is told. */}
          <p
            role="status"
            className="min-w-0 flex-1 text-[13px] 3xl:text-[14px] text-ink-muted"
          >
            We could not load your booked calls. {error}
          </p>
          {onRetry && (
            <Button
              size="sm"
              variant="secondary"
              onClick={onRetry}
              // The sentence beside it takes the whole card on a phone, so the
              // retry is on a line of its own either way — full width there
              // rather than stranded at the left edge of it.
              className="w-full md:w-auto"
            >
              Try again
            </Button>
          )}
        </div>
      </Section>
    );
  }

  if (meetings.length === 0) {
    return (
      <Section>
        {/* Said plainly rather than by drawing nothing. "You have not booked
            one" is an answer; an absent section is a screen the reader has to
            guess at — and the guess they make is that it is broken.

            The welcome layout rather than the grey line this used to be: a
            history with no rows in it is a workspace that has never asked us
            for anything, which is `EmptyState`'s first-run case and not a list
            reporting a result. No highlights — the form directly above already
            says what booking a call gets you, and repeating it under the mark
            would be the same offer made twice on one tab — so the block centres
            in the space the way the compact state does.

            No card around it either: there are no rows for a card to hold, and
            an outlined box drawn around an illustration reads as a row that
            failed to fill rather than as a section at rest. */}
        <EmptyState
          variant="welcome"
          // Set to this tab's own type scale rather than a first-run screen's.
          // The history is the last thing on a tab that already has a heading
          // and a form on it, and at page size the headline here outshouts
          // both — an empty list is a footnote to this screen, not its point.
          size="sm"
          illustration={<NoHistoryMark aria-hidden className="h-12 w-12" />}
          title="No support requests yet"
          // Broken after the first sentence as the design has it: one says
          // nothing is wrong, the other says where these will turn up, and each
          // is easier to take in on its own line. It still wraps normally on a
          // narrow screen.
          description={
            <>
              You haven&apos;t reached out to our support team yet.
              <br />
              When you do, your conversations will appear here.
            </>
          }
        />
      </Section>
    );
  }

  return (
    <Section>
      <ul className="flex flex-col gap-3">
        {meetings.map((meeting) => (
          <MeetingRow key={meeting.id} meeting={meeting} />
        ))}
      </ul>
    </Section>
  );
};

export default SupportHistory;
